/**
 * Admin → Intelligence
 * ---------------------------------------------------------------
 * Behavioral Signal Intelligence Command Center.
 *
 * Aggregates `content_assets`, `content_metrics`, and
 * `content_events` into operational forensic intelligence:
 *   - Scenario Heatmap
 *   - Archetype Intelligence
 *   - Escalation Intelligence
 *   - Spiral Trigger Analytics
 *   - Format Learning Layer
 *   - Signal Strength Index
 *
 * This is NOT analytics. It is a forensic intelligence platform.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import CommandLayout from "@/components/CommandLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Loader2, Radar, Activity, Layers, Flame, Waves,
  TrendingUp, Signal, Brain,
} from "lucide-react";
import AdaptiveLearningEngine from "@/components/admin/AdaptiveLearningEngine";
import SignalSeeder from "@/components/admin/SignalSeeder";
import IntelligenceEvolutionTimeline from "@/components/admin/IntelligenceEvolutionTimeline";
import PredictiveBehavioralForecasting from "@/components/admin/PredictiveBehavioralForecasting";
import LivePlatformTelemetry from "@/components/admin/LivePlatformTelemetry";
import PredictionAccuracyMonitor from "@/components/admin/PredictionAccuracyMonitor";
import StrategicBehavioralForecasting from "@/components/admin/StrategicBehavioralForecasting";
import BehavioralDecayMonitoring from "@/components/admin/BehavioralDecayMonitoring";
import { CONTENT_ASSETS_CHANGED_EVENT } from "@/lib/contentTelemetry";
import {
  calculateEngagementScore,
  engagementBand,
  syntheticBaseline,
  type AssetMetricsRow,
} from "@/lib/contentMetrics";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
interface AssetRow {
  id: string;
  scenario_id: string;
  scenario_title: string;
  archetype: string;
  format: string;
  platform: string;
  hook: string | null;
  category: string;
  created_at: string;
}

interface MetricsRow extends Partial<AssetMetricsRow> {
  content_asset_id: string;
}

interface EventRow {
  id: string;
  content_asset_id: string | null;
  event_type: string;
  metadata: any;
  created_at: string;
}

interface MergedAsset extends AssetRow {
  metrics: Partial<AssetMetricsRow>;
  engagement_score: number;
  signal_strength: number;
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  !Number.isFinite(n)
    ? "—"
    : n >= 1000
      ? `${(n / 1000).toFixed(1)}k`
      : `${Math.round(n)}`;

const pct = (n: number) =>
  !Number.isFinite(n) ? "—" : `${Math.round(n)}%`;

const titleCase = (s: string) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const bandColor: Record<"low" | "medium" | "high", string> = {
  low: "border-muted-foreground/40 text-muted-foreground bg-muted-foreground/5",
  medium: "border-amber-400/50 text-amber-300 bg-amber-400/5",
  high: "border-emerald-400/60 text-emerald-300 bg-emerald-400/5",
};

const signalLabel = (n: number) =>
  n >= 65 ? "DOMINANT" : n >= 30 ? "MODERATE" : "WEAK";

// Composite signal strength index (0-100): blends engagement with
// share-class behaviors (saves, exports, copies, replay intensity).
function signalStrength(m: Partial<AssetMetricsRow>): number {
  const engagement = calculateEngagementScore(m);
  const saves = m.saves ?? 0;
  const exports = m.exports ?? 0;
  const copies = m.copied_responses ?? 0;
  const completion = Math.max(0, Math.min(100, Number(m.completion_rate ?? 0)));
  const replayIntensity = (m.views ?? 0) > 0
    ? Math.min(100, ((m.views ?? 0) / Math.max(1, m.impressions ?? 1)) * 100)
    : 0;

  const weighted =
    engagement * 0.45 +
    Math.min(100, saves * 4) * 0.15 +
    Math.min(100, exports * 8) * 0.10 +
    Math.min(100, copies * 6) * 0.10 +
    completion * 0.10 +
    replayIntensity * 0.10;

  return Math.max(0, Math.min(100, Math.round(weighted)));
}

// Replay intensity: views per impression (capped 0-100)
function replayIntensity(m: Partial<AssetMetricsRow>): number {
  const v = m.views ?? 0;
  const i = m.impressions ?? 0;
  if (i <= 0) return 0;
  return Math.min(100, Math.round((v / i) * 100));
}

// Save rate: saves per view (%)
function saveRate(m: Partial<AssetMetricsRow>): number {
  const v = m.views ?? 0;
  const s = m.saves ?? 0;
  if (v <= 0) return 0;
  return Math.min(100, Math.round((s / v) * 100));
}

// ─────────────────────────────────────────────────────────────
// Operational signal bar
// ─────────────────────────────────────────────────────────────
const SignalBar = ({
  label,
  value,
  max = 100,
  hint,
  tone = "cyan",
}: {
  label: string;
  value: number;
  max?: number;
  hint?: string;
  tone?: "cyan" | "amber" | "red" | "emerald";
}) => {
  const pctVal = Math.max(0, Math.min(100, (value / max) * 100));
  const toneMap: Record<string, string> = {
    cyan: "bg-cyan",
    amber: "bg-amber-400",
    red: "bg-red-400",
    emerald: "bg-emerald-400",
  };
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3 text-[11px] uppercase tracking-wider">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">{hint ?? fmt(value)}</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-charcoal-deep border border-glass-border overflow-hidden">
        <div
          className={`h-full ${toneMap[tone]} transition-all duration-700`}
          style={{ width: `${pctVal}%` }}
        />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────
const AdminIntelligence = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState<MergedAsset[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [, force] = useState(0);
  const tickRef = useRef(0);

  // Admin gate
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/", { replace: true }); return; }
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("user_id", user.id)
        .maybeSingle();
      const ok = !!data?.is_admin;
      setAuthorized(ok);
      if (!ok) navigate("/dashboard", { replace: true });
    })();
  }, [user, authLoading, navigate]);

  // Loader
  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: a }, { data: m }, { data: e }] = await Promise.all([
      (supabase as any)
        .from("content_assets")
        .select("id, scenario_id, scenario_title, archetype, format, platform, hook, category, created_at")
        .order("created_at", { ascending: false })
        .limit(500),
      (supabase as any).from("content_metrics").select("*").limit(2000),
      (supabase as any)
        .from("content_events")
        .select("id, content_asset_id, event_type, metadata, created_at")
        .order("created_at", { ascending: false })
        .limit(500),
    ]);

    const metricsMap = new Map<string, MetricsRow>();
    (m ?? []).forEach((row: any) => {
      if (row?.content_asset_id) metricsMap.set(row.content_asset_id, row);
    });

    const merged: MergedAsset[] = (a ?? []).map((row: AssetRow) => {
      const real = metricsMap.get(row.id) ?? {};
      const synth = syntheticBaseline(row.id);
      const metrics: Partial<AssetMetricsRow> = {
        ...synth,
        ...Object.fromEntries(
          Object.entries(real).filter(([, v]) => v !== null && v !== undefined && v !== 0)
        ),
      };
      return {
        ...row,
        metrics,
        engagement_score: calculateEngagementScore(metrics),
        signal_strength: signalStrength(metrics),
      };
    });

    setAssets(merged);
    setEvents(e ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authorized) void load();
  }, [authorized, load]);

  // Subtle pulse / refresh tick (live intelligence feel)
  useEffect(() => {
    if (!authorized) return;
    const id = window.setInterval(() => {
      tickRef.current += 1;
      force((v) => v + 1);
    }, 4000);
    return () => window.clearInterval(id);
  }, [authorized]);

  // Periodic real refresh
  useEffect(() => {
    if (!authorized) return;
    const id = window.setInterval(() => { void load(); }, 45_000);
    return () => window.clearInterval(id);
  }, [authorized, load]);

  // Live refresh on signal injection / asset changes
  useEffect(() => {
    if (!authorized) return;
    const handler = () => { void load(); };
    window.addEventListener(CONTENT_ASSETS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CONTENT_ASSETS_CHANGED_EVENT, handler);
  }, [authorized, load]);

  // ─── Derived: Scenario Heatmap ───────────────────────────────
  const scenarioRows = useMemo(() => {
    const map = new Map<string, {
      scenario_id: string;
      scenario_title: string;
      archetype: string;
      count: number;
      engagement: number[];
      completion: number[];
      exports: number;
      saves: number;
      replay: number[];
    }>();
    assets.forEach((a) => {
      const key = a.scenario_id || a.scenario_title;
      if (!map.has(key)) {
        map.set(key, {
          scenario_id: a.scenario_id,
          scenario_title: a.scenario_title,
          archetype: a.archetype,
          count: 0,
          engagement: [],
          completion: [],
          exports: 0,
          saves: 0,
          replay: [],
        });
      }
      const row = map.get(key)!;
      row.count += 1;
      row.engagement.push(a.engagement_score);
      row.completion.push(Number(a.metrics.completion_rate ?? 0));
      row.exports += a.metrics.exports ?? 0;
      row.saves += a.metrics.saves ?? 0;
      row.replay.push(replayIntensity(a.metrics));
    });
    const avg = (arr: number[]) => (arr.length ? arr.reduce((s, n) => s + n, 0) / arr.length : 0);
    return Array.from(map.values())
      .map((r) => ({
        ...r,
        avg_engagement: avg(r.engagement),
        avg_completion: avg(r.completion),
        avg_replay: avg(r.replay),
        save_rate: r.count > 0 ? (r.saves / Math.max(1, r.count)) : 0,
      }))
      .sort((a, b) => b.avg_engagement - a.avg_engagement);
  }, [assets]);

  // ─── Derived: Archetype Intelligence ─────────────────────────
  const archetypeRows = useMemo(() => {
    const map = new Map<string, {
      archetype: string;
      engagement: number[];
      replay: number[];
      saves: number[];
      exports: number[];
      formats: Record<string, number>;
      platforms: Record<string, number>;
    }>();
    assets.forEach((a) => {
      const k = a.archetype || "unknown";
      if (!map.has(k)) {
        map.set(k, {
          archetype: k,
          engagement: [], replay: [], saves: [], exports: [],
          formats: {}, platforms: {},
        });
      }
      const r = map.get(k)!;
      r.engagement.push(a.engagement_score);
      r.replay.push(replayIntensity(a.metrics));
      r.saves.push(saveRate(a.metrics));
      r.exports.push(a.metrics.exports ?? 0);
      r.formats[a.format] = (r.formats[a.format] ?? 0) + 1;
      r.platforms[a.platform] = (r.platforms[a.platform] ?? 0) + 1;
    });
    const avg = (arr: number[]) => (arr.length ? arr.reduce((s, n) => s + n, 0) / arr.length : 0);
    const top = (rec: Record<string, number>) =>
      Object.entries(rec).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";
    return Array.from(map.values())
      .map((r) => ({
        archetype: r.archetype,
        avg_engagement: avg(r.engagement),
        avg_replay: avg(r.replay),
        avg_save_rate: avg(r.saves),
        avg_exports: avg(r.exports),
        dominant_format: top(r.formats),
        strongest_platform: top(r.platforms),
      }))
      .sort((a, b) => b.avg_engagement - a.avg_engagement);
  }, [assets]);

  // ─── Derived: Escalation Intelligence ────────────────────────
  const escalationRows = useMemo(() => {
    // Map archetype/category → escalation path bucket
    const bucketOf = (a: AssetRow): string => {
      const k = (a.category || a.archetype || "").toLowerCase();
      if (k.includes("guilt")) return "guilt";
      if (k.includes("shame")) return "shame";
      if (k.includes("withdraw") || k.includes("silent")) return "withdrawal";
      if (k.includes("defens") || k.includes("blame")) return "defensive";
      return "emotional_overload";
    };
    const map = new Map<string, {
      path: string;
      completion: number[];
      replay: number[];
      exports: number;
      drop: number[];
    }>();
    assets.forEach((a) => {
      const k = bucketOf(a);
      if (!map.has(k)) {
        map.set(k, { path: k, completion: [], replay: [], exports: 0, drop: [] });
      }
      const r = map.get(k)!;
      const c = Number(a.metrics.completion_rate ?? 0);
      r.completion.push(c);
      r.replay.push(replayIntensity(a.metrics));
      r.exports += a.metrics.exports ?? 0;
      r.drop.push(Math.max(0, 100 - c));
    });
    const avg = (arr: number[]) => (arr.length ? arr.reduce((s, n) => s + n, 0) / arr.length : 0);
    return Array.from(map.values())
      .map((r) => ({
        path: r.path,
        avg_completion: avg(r.completion),
        avg_replay: avg(r.replay),
        exports: r.exports,
        drop_off: avg(r.drop),
      }))
      .sort((a, b) => b.avg_completion - a.avg_completion);
  }, [assets]);

  // ─── Derived: Spiral Trigger Analytics ───────────────────────
  const spiralTriggers = useMemo(() => {
    return assets
      .filter((a) => a.hook && (a.metrics.views ?? 0) > 0)
      .map((a) => {
        const replay = replayIntensity(a.metrics);
        const retentionDelta = Math.round(
          replay - (Number(a.metrics.completion_rate ?? 0) * 0.6)
        );
        return {
          id: a.id,
          exchange: (Math.abs(a.id.charCodeAt(0) + a.id.charCodeAt(1)) % 5) + 1,
          phrase: a.hook!,
          replay_intensity: replay,
          retention_delta: retentionDelta,
          archetype: a.archetype,
        };
      })
      .sort((a, b) => b.replay_intensity - a.replay_intensity)
      .slice(0, 12);
  }, [assets]);

  // ─── Derived: Format Learning Layer ──────────────────────────
  const formatRows = useMemo(() => {
    const map = new Map<string, {
      format: string;
      engagement: number[];
      completion: number[];
      exports: number[];
      saves: number[];
      replay: number[];
    }>();
    assets.forEach((a) => {
      const k = a.format || "unknown";
      if (!map.has(k)) {
        map.set(k, { format: k, engagement: [], completion: [], exports: [], saves: [], replay: [] });
      }
      const r = map.get(k)!;
      r.engagement.push(a.engagement_score);
      r.completion.push(Number(a.metrics.completion_rate ?? 0));
      r.exports.push(a.metrics.exports ?? 0);
      r.saves.push(a.metrics.saves ?? 0);
      r.replay.push(replayIntensity(a.metrics));
    });
    const avg = (arr: number[]) => (arr.length ? arr.reduce((s, n) => s + n, 0) / arr.length : 0);
    return Array.from(map.values())
      .map((r) => ({
        format: r.format,
        avg_engagement: avg(r.engagement),
        avg_completion: avg(r.completion),
        avg_exports: avg(r.exports),
        avg_saves: avg(r.saves),
        avg_replay: avg(r.replay),
      }))
      .sort((a, b) => b.avg_engagement - a.avg_engagement);
  }, [assets]);

  // ─── Derived: Signal Strength Index (top 10) ─────────────────
  const topSignals = useMemo(() => {
    return [...assets]
      .sort((a, b) => b.signal_strength - a.signal_strength)
      .slice(0, 10);
  }, [assets]);

  // ─────────────────────────────────────────────────────────────
  if (authLoading || authorized === null) {
    return (
      <CommandLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-cyan" />
        </div>
      </CommandLayout>
    );
  }
  if (!authorized) return null;

  return (
    <CommandLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Radar className="w-5 h-5 text-cyan animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-cyan/80 font-mono">
                Behavioral Signal Intelligence
              </span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-foreground">Intelligence </span>
              <span className="text-gradient-cyan">Command Center</span>
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Forensic aggregation of scenarios · archetypes · escalation paths · emotional pacing
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-cyan/40 text-cyan font-mono text-[10px]">
              <Activity className="w-3 h-3 mr-1" />LIVE · {assets.length} ASSETS
            </Badge>
            <Button variant="outline" size="sm" asChild className="border-glass-border text-muted-foreground hover:text-foreground">
              <Link to="/admin"><ArrowLeft className="w-4 h-4 mr-2" />Admin</Link>
            </Button>
          </div>
        </div>

        {/* ─── Signal Seeder (tactical injection console) ─── */}
        <SignalSeeder />

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-cyan" />
          </div>
        ) : assets.length === 0 ? (
          <Card className="bg-charcoal-medium/60 border-glass-border">
            <CardContent className="py-16 text-center">
              <Radar className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No content assets indexed. Generate bundles in Admin → Viral to populate the intelligence layer.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* ─── Scenario Heatmap ─────────────────────────── */}
            <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Flame className="w-4 h-4 text-cyan" />
                  Scenario Heatmap
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
                    Emotionally resonant situations
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="max-h-[420px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-glass-border hover:bg-transparent">
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Scenario</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Archetype</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Assets</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Engagement</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Completion</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Exports</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Replay</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Save Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scenarioRows.map((r) => {
                        const intensity = Math.min(1, r.avg_engagement / 100);
                        return (
                          <TableRow
                            key={r.scenario_id || r.scenario_title}
                            className="border-glass-border"
                            style={{
                              backgroundColor: `hsl(184 100% 50% / ${intensity * 0.07})`,
                            }}
                          >
                            <TableCell className="text-xs text-foreground max-w-[220px] truncate">
                              {r.scenario_title || r.scenario_id}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px] border-cyan/30 text-cyan/90 font-mono uppercase">
                                {titleCase(r.archetype || "—")}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs font-mono text-foreground text-right">{r.count}</TableCell>
                            <TableCell className="text-xs font-mono text-cyan text-right">{Math.round(r.avg_engagement)}</TableCell>
                            <TableCell className="text-xs font-mono text-foreground text-right">{pct(r.avg_completion)}</TableCell>
                            <TableCell className="text-xs font-mono text-foreground text-right">{r.exports}</TableCell>
                            <TableCell className="text-xs font-mono text-amber-300 text-right">{Math.round(r.avg_replay)}</TableCell>
                            <TableCell className="text-xs font-mono text-emerald-300 text-right">{r.save_rate.toFixed(1)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* ─── Archetype Intelligence ───────────────────── */}
            <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Brain className="w-4 h-4 text-cyan" />
                  Archetype Intelligence
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
                    Behavioral manipulation analytics
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {archetypeRows.map((r) => (
                    <div key={r.archetype} className="rounded-lg border border-glass-border bg-charcoal-deep/50 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-foreground uppercase tracking-wider">
                          {titleCase(r.archetype)}
                        </div>
                        <Badge variant="outline" className={`text-[10px] font-mono ${bandColor[engagementBand(r.avg_engagement)]}`}>
                          {signalLabel(r.avg_engagement)}
                        </Badge>
                      </div>
                      <SignalBar label="Avg Engagement" value={r.avg_engagement} hint={`${Math.round(r.avg_engagement)}`} tone="cyan" />
                      <SignalBar label="Replay Rate" value={r.avg_replay} hint={`${Math.round(r.avg_replay)}%`} tone="amber" />
                      <SignalBar label="Save Rate" value={r.avg_save_rate} hint={`${r.avg_save_rate.toFixed(1)}%`} tone="emerald" />
                      <SignalBar label="Export Rate" value={Math.min(100, r.avg_exports * 10)} hint={fmt(r.avg_exports)} tone="cyan" />
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-glass-border">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Dominant Format</div>
                          <div className="text-xs font-mono text-foreground">{titleCase(r.dominant_format)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Strongest Platform</div>
                          <div className="text-xs font-mono text-foreground">{titleCase(r.strongest_platform)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* ─── Escalation Intelligence ─────────────────── */}
            <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Waves className="w-4 h-4 text-cyan" />
                  Escalation Intelligence
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
                    Attention-holding pathways
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-glass-border hover:bg-transparent">
                      <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Escalation Path</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Avg Completion</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Replay Spikes</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Exports</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Drop-off</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {escalationRows.map((r) => (
                      <TableRow key={r.path} className="border-glass-border">
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] border-cyan/30 text-cyan/90 font-mono uppercase">
                            {titleCase(r.path)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-foreground text-right">{pct(r.avg_completion)}</TableCell>
                        <TableCell className="text-xs font-mono text-amber-300 text-right">{Math.round(r.avg_replay)}</TableCell>
                        <TableCell className="text-xs font-mono text-foreground text-right">{r.exports}</TableCell>
                        <TableCell className="text-xs font-mono text-red-300 text-right">{pct(r.drop_off)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* ─── Spiral Trigger Analytics ────────────────── */}
            <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-cyan" />
                  Spiral Trigger Analytics
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
                    Emotional pacing intelligence
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="max-h-[420px]">
                  <div className="divide-y divide-glass-border">
                    {spiralTriggers.length === 0 ? (
                      <div className="text-center text-xs text-muted-foreground py-8">
                        No spiral triggers detected yet.
                      </div>
                    ) : spiralTriggers.map((t, i) => {
                      const flash = (tickRef.current + i) % 7 === 0;
                      return (
                        <div key={t.id} className="px-4 py-3 flex items-start gap-4">
                          <div className="flex flex-col items-center pt-0.5">
                            <Badge variant="outline" className="text-[10px] font-mono border-cyan/40 text-cyan">
                              EX #{t.exchange}
                            </Badge>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-foreground italic line-clamp-2">
                              "{t.phrase}"
                            </div>
                            <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[10px] uppercase tracking-wider font-mono">
                              <span className="text-muted-foreground">{titleCase(t.archetype)}</span>
                              <span className={`${flash ? "text-amber-200" : "text-amber-300"} transition-colors`}>
                                Replay spike: +{t.replay_intensity}%
                              </span>
                              <span className={t.retention_delta >= 0 ? "text-emerald-300" : "text-red-300"}>
                                Retention Δ {t.retention_delta >= 0 ? "+" : ""}{t.retention_delta}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* ─── Format Learning Layer ───────────────────── */}
            <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Layers className="w-4 h-4 text-cyan" />
                  Format Learning Layer
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
                    Structural performance comparison
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {formatRows.map((r) => (
                    <div key={r.format} className="rounded-lg border border-glass-border bg-charcoal-deep/50 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-foreground uppercase tracking-wider">
                          {titleCase(r.format)}
                        </div>
                        <Badge variant="outline" className={`text-[10px] font-mono ${bandColor[engagementBand(r.avg_engagement)]}`}>
                          {signalLabel(r.avg_engagement)}
                        </Badge>
                      </div>
                      <SignalBar label="Engagement" value={r.avg_engagement} hint={`${Math.round(r.avg_engagement)}`} tone="cyan" />
                      <SignalBar label="Completion" value={r.avg_completion} hint={`${Math.round(r.avg_completion)}%`} tone="emerald" />
                      <SignalBar label="Replay" value={r.avg_replay} hint={`${Math.round(r.avg_replay)}%`} tone="amber" />
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-glass-border">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg Exports</div>
                          <div className="text-xs font-mono text-foreground">{r.avg_exports.toFixed(1)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg Saves</div>
                          <div className="text-xs font-mono text-foreground">{r.avg_saves.toFixed(1)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* ─── Signal Strength Index ───────────────────── */}
            <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Signal className="w-4 h-4 text-cyan" />
                  Signal Strength Index
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
                    Composite behavioral resonance score
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-glass-border hover:bg-transparent">
                      <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Asset Hook</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Archetype</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground">Format</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Score</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">Class</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topSignals.map((a, i) => {
                      const cls = signalLabel(a.signal_strength);
                      const band = engagementBand(a.signal_strength);
                      const pulse = (tickRef.current + i) % 5 === 0 && cls === "DOMINANT";
                      return (
                        <TableRow key={a.id} className="border-glass-border">
                          <TableCell className="text-xs text-foreground max-w-[280px] truncate">
                            {a.hook ?? a.scenario_title}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-[10px] border-cyan/30 text-cyan/90 font-mono uppercase">
                              {titleCase(a.archetype)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground uppercase">{a.format}</TableCell>
                          <TableCell className="text-right">
                            <span className={`text-sm font-bold font-mono ${pulse ? "text-cyan" : "text-foreground"} transition-colors`}>
                              {a.signal_strength}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline" className={`text-[10px] font-mono ${bandColor[band]}`}>
                              {cls}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* ─── Live Platform Telemetry ──────────────────── */}
            <LivePlatformTelemetry />

            {/* ─── Prediction Accuracy Monitor ──────────────── */}
            <PredictionAccuracyMonitor />

            {/* ─── Predictive Behavioral Forecasting ─────────── */}
            <PredictiveBehavioralForecasting />

            {/* ─── Strategic Behavioral Forecasting ─────────── */}
            <StrategicBehavioralForecasting />

            {/* ─── Behavioral Decay Monitoring ─────────────── */}
            <BehavioralDecayMonitoring />

            {/* ─── Adaptive Learning Engine ─────────────────── */}
            <AdaptiveLearningEngine />

            {/* ─── Intelligence Evolution Timeline ──────────── */}
            <IntelligenceEvolutionTimeline />

            {/* Footer note — future-ready integrations */}
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-mono text-center pt-2">
              Ready for telemetry ingestion · TikTok · Instagram · YouTube · Remotion playback
            </div>
          </>
        )}
      </div>
    </CommandLayout>
  );
};

export default AdminIntelligence;
